import { useQueryClient } from "react-query";
import { SessionExpirationModal } from "../AuthProvider";
import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";
import { ErrorBoundary } from "react-error-boundary";
import { AxiosError } from "axios";
import { usePostHog } from "posthog-js/react";
import { CORE_ANALYTICS_EVENTS } from "@/lib/posthog-constants";

interface BSErrorBoundaryProps {
  children: React.ReactNode;
  title: string;
  FallbackComponent?: React.ComponentType<{ error: any }>;
  fallback?:
    | React.ReactNode
    | ((props: {
        error: any;
        resetErrorBoundary: () => void;
      }) => React.ReactNode);
  /** Leaves an error state when any entry changes, like react-error-boundary. */
  resetKeys?: unknown[];
  onReset?: () => void;
  /** Once per caught error, after it is reported. */
  onError?: (error: Error) => void;
  /** Tags on the Sentry event, e.g. `{ feature: "diff_viewer" }`; a function derives them from the caught error. */
  sentryTags?: SentryTags | ((error: Error) => SentryTags);
}

type SentryTags = Record<string, string>;

const DefaultFallback = () => (
  <div className="h-[300px] border border-dashed rounded-lg flex items-center justify-center text-neutral-300 dark:text-neutral-600 text-sm">
    Something went wrong, please try refreshing the page
  </div>
);

export function BSErrorBoundary({
  children,
  title,
  FallbackComponent = DefaultFallback,
  fallback,
  resetKeys,
  onReset,
  onError,
  sentryTags,
}: BSErrorBoundaryProps) {
  const queryClient = useQueryClient();
  const postHog = usePostHog();

  const report = (error: any) => {
    const data = queryClient.getQueryData("connectedGithubOrgs");

    const activeGithubOrg =
      (data as { active_org_name: string | null })?.active_org_name ?? null;

    const tags =
      typeof sentryTags === "function" ? sentryTags(error) : sentryTags;

    Sentry.withScope((scope) => {
      if (activeGithubOrg) {
        scope.setExtra("activeGithubOrg", activeGithubOrg);
      }
      for (const [tag, value] of Object.entries(tags ?? {})) {
        scope.setTag(tag, value);
      }
      scope.setExtra("errorBoundaryTitle", title);
      const errorWithTitle = new Error(`[${title}] ${error.message}`);
      errorWithTitle.stack = error.stack;
      Sentry.captureException(errorWithTitle);
    });

    console.error(`[${title}] error boundary triggered with error`, error);
    postHog?.capture(CORE_ANALYTICS_EVENTS.ERROR, {
      error: error,
      errorBoundaryTitle: title,
      orgName: activeGithubOrg,
      stackTrace: error?.stack || undefined,
    });
    onError?.(error);
  };

  const renderFallback = (error: any, resetErrorBoundary: () => void) => {
    if (error instanceof AxiosError && error.response?.status === 401) {
      return <SessionExpirationModal open={true} setOpen={() => {}} />;
    }

    if (fallback !== undefined) {
      if (typeof fallback === "function") {
        return fallback({ error, resetErrorBoundary });
      }
      return fallback;
    }

    return <FallbackComponent error={error} />;
  };

  return (
    <ErrorBoundary
      // componentDidCatch runs once per caught error; the fallback renders again with every parent render.
      onError={report}
      fallbackRender={({ error, resetErrorBoundary }) =>
        renderFallback(error, resetErrorBoundary)
      }
      resetKeys={resetKeys}
      onReset={onReset}
    >
      {children}
    </ErrorBoundary>
  );
}
